import { useState, useMemo, useEffect, useRef } from "react";
import {
  StandardDialog,
  StandardDialogContent,
  StandardDialogHeader,
  StandardDialogBody,
  StandardDialogTitle,
} from "@/components/ui/standard-dialog";
import { Input } from "@/components/ui/input";
import {
  AutocompleteCombobox,
  type AutocompleteOption,
} from "@/components/ui/autocomplete-combobox";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Search, Leaf, Loader2 } from "lucide-react";
import {
  useEmissionsSearchInfinite,
  useEmissionsEstimate,
  type EmissionsProvider,
} from "@/hooks/useEmissionsProxy";
import { useDebounce } from "@/hooks/useDebounce";
import { useTranslation } from "@/hooks/useTranslation";
import { toast } from "@/hooks/useToast";
import { captureMessage } from "@/lib/sentry";
import {
  climatiqUnitTypeMapping,
  isSupportedClimatiqUnitType,
  prettyLca,
} from "@/lib/climatiqUnitTypes";
import {
  groupClimatiqResultsByYear,
  type FactorGroup,
} from "@/lib/climatiqGrouping";

/**
 * A factor resolved to a per-unit value, ready to snapshot into the org library.
 * For a Climatiq pick this is captured via an estimate at quantity 1 of the
 * factor's unit type (→ per-unit value); for a local pick the stored value is
 * used directly. `sector`/`category` come from Climatiq's taxonomy and drive the
 * per-sector table + its dimension; `unit_type` is the Climatiq unit type the
 * `input_unit_code` was derived from.
 */
export interface PickedEmissionFactor {
  provider: EmissionsProvider;
  /** Climatiq activity_id, or the local factor's id. */
  external_activity_id: string;
  factor_name: string;
  /** Per-unit value (kg CO₂e per input unit). */
  emission_value: number;
  unit_of_measure: string;
  input_unit_code: string;
  output_unit_code: string;
  year: number | null;
  /** Climatiq region id (e.g. "GB") — used for the estimate call. */
  region: string | null;
  /** Human-readable region name (e.g. "United Kingdom") — used as the
   *  `region` dimension value/label. Falls back to the id when absent. */
  region_name: string | null;
  /** Climatiq data source / publisher (e.g. "BEIS", "EPA") — the `data_source`
   *  dimension value. Null for local picks. */
  data_source: string | null;
  external_data_version: string | null;
  scopes: string[];
  /** Climatiq taxonomy — null for local picks. */
  sector: string | null;
  category: string | null;
  unit_type: string | null;
  /** Climatiq LCA boundary (well_to_tank | tank_to_wheel | well_to_wheel | …) —
   *  distinguishes otherwise-identical factor rows. */
  source_lca_activity: string | null;
}

interface EmissionFactorPickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Pre-seed the search box (e.g. "cardboard recycling"). */
  defaultQuery?: string;
  /** Default Climatiq region filter (org locale). */
  defaultRegion?: string;
  /**
   * Called with one or more factors to import. A Climatiq row collapses every
   * year of the same factor (same activity/region/source/LCA), so using it
   * yields one factor per available year — the consumer saves them all and lets
   * the user pick the year afterwards. Local picks pass a single factor.
   */
  onPicked: (factors: PickedEmissionFactor[]) => void;
}

// Large pages mean far fewer round-trips to Climatiq (its documented max is 500).
// A handful of dense pages beats dozens of 25-row pages — especially with the
// client-side "common only" filter discarding much of each raw page.
const RESULTS_PER_PAGE = 200;
// Stop auto-paginating after this many pages so a broad query doesn't pull page
// after page. With 200-row pages a couple of pages is already plenty; past the
// cap the user must click "Load more" (and is nudged to refine the search).
const MAX_AUTOLOAD_PAGES = 3;

/** Display a metadata cell value, em-dash for empty. */
const cell = (v: unknown): string => (v == null || v === "" ? "—" : String(v));

// Freight / logistics unit types most (non-logistics) clients don't need — e.g.
// rail/road freight is tonne-km (WeightOverDistance). Passenger travel
// (PassengerOverDistance) is intentionally NOT here. Plus rarely-used compounds.
const NICHE_UNIT_TYPES = new Set([
  "weightoverdistance",
  "containeroverdistance",
  "volumeoverdistance",
  "weightovertime",
  "areaovertime",
  "dataovertime",
  "distanceovertime",
  "numberovertime",
]);
// Partial lifecycle boundaries — keep the total (well-to-wheel) and unscoped
// factors; hide the upstream-only / combustion-only partials.
const PARTIAL_LCA = new Set(["well_to_tank", "tank_to_wheel"]);

/** The "common factors only" smart default: exclude spend-based, freight/niche
 *  unit types, partial-LCA-boundary variants, and supplier-specific factors
 *  (e.g. per-utility grid mixes) that flood broad searches like "electricity". */
export function isCommonFactor(r: Record<string, unknown>): boolean {
  const ut = typeof r.unit_type === "string" ? r.unit_type.toLowerCase() : "";
  if (ut === "money" || NICHE_UNIT_TYPES.has(ut)) return false;
  const iu =
    typeof r.input_unit_code === "string" ? r.input_unit_code.toLowerCase() : "";
  if (["eur", "usd", "gbp", "money"].includes(iu)) return false;
  const lca =
    typeof r.source_lca_activity === "string" ? r.source_lca_activity : "";
  if (PARTIAL_LCA.has(lca)) return false;
  // Supplier-specific variants (one row per utility) are noise for most users,
  // who want the grid/regional average.
  const name = typeof r.name === "string" ? r.name.toLowerCase() : "";
  const aid = typeof r.activity_id === "string" ? r.activity_id.toLowerCase() : "";
  if (name.includes("supplier") || aid.includes("supplier")) return false;
  return true;
}

/**
 * Generic emission-factor picker. Local-first (the NL co2emissiefactoren.nl seed)
 * with a Climatiq fallback; dynamic year selector from Climatiq's possible_filters;
 * a Climatiq pick estimates at quantity 1 of the factor's unit type. Results are
 * shown in a metadata table with infinite scroll.
 *
 * Conditionally mounts its body so the search/estimate hooks only fire while open.
 */
export function EmissionFactorPickerDialog({
  open,
  onOpenChange,
  ...rest
}: EmissionFactorPickerDialogProps) {
  const { t } = useTranslation();
  return (
    <StandardDialog open={open} onOpenChange={onOpenChange}>
      <StandardDialogContent size="4xl">
        <StandardDialogHeader>
          <StandardDialogTitle>{t("emissions.lookupTitle")}</StandardDialogTitle>
        </StandardDialogHeader>
        <StandardDialogBody>
          {open && <PickerBody onOpenChange={onOpenChange} {...rest} />}
        </StandardDialogBody>
      </StandardDialogContent>
    </StandardDialog>
  );
}

function PickerBody({
  defaultQuery,
  defaultRegion = "",
  onPicked,
  onOpenChange,
}: Omit<EmissionFactorPickerDialogProps, "open">) {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState(defaultQuery ?? "");
  // This modal exists to look factors up EXTERNALLY, so default to Climatiq.
  // The toggle still lets a user switch to "Your data" to reuse saved factors.
  const [provider, setProvider] = useState<EmissionsProvider>("climatiq");
  const [region, setRegion] = useState(defaultRegion);
  const [year, setYear] = useState<string>("");
  const [pendingId, setPendingId] = useState<string | null>(null);
  // Smart default: show only the "basics" most clients need. Hides three kinds of
  // niche factor — spend-based (money), freight/logistics (tonne-km etc.), and
  // partial lifecycle boundaries (well-to-tank / tank-to-wheel, keeping the total
  // well-to-wheel). Toggle off to see everything.
  const [commonOnly, setCommonOnly] = useState(true);

  const debounced = useDebounce(searchTerm, 400);
  const enabled = debounced.length >= 3;

  const params = useMemo(() => {
    const p: Record<string, unknown> = { query: debounced };
    if (provider === "climatiq") {
      if (region) p.region = region;
      if (year) p.year = Number(year);
    }
    return p;
  }, [debounced, provider, region, year]);

  const {
    data,
    isLoading,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useEmissionsSearchInfinite({ provider, params, enabled, pageSize: RESULTS_PER_PAGE });
  const estimate = useEmissionsEstimate();

  // Flatten accumulated pages.
  const results = useMemo(
    () => data?.pages.flatMap((p) => p.data?.results ?? []) ?? [],
    [data],
  );
  const possibleYears =
    (data?.pages[0]?.data?.possible_filters?.year as number[] | undefined) ?? [];
  // Climatiq returns the available regions for the query as { id, name } facets
  // (id is the filter value sent back, e.g. "GB"). Drive a searchable dropdown
  // off them, with an "Any region" sentinel (value "") to clear the filter.
  const possibleRegions = data?.pages[0]?.data?.possible_filters?.region ?? [];
  const regionOptions = useMemo<AutocompleteOption[]>(
    () => [
      { id: "__any__", name: t("emissions.anyRegion"), value: "" },
      ...possibleRegions
        .map((r) => ({ id: r.id, name: r.name, value: r.id }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    ],
    // possibleRegions is re-derived from `data` each render; key the memo on data.
    [data, t], // eslint-disable-line react-hooks/exhaustive-deps
  );

  // Gate auto-pagination: only keep auto-loading until the page cap. Past it the
  // user loads more explicitly (and is nudged to refine the search).
  const autoloadCapped = (data?.pages.length ?? 0) >= MAX_AUTOLOAD_PAGES;
  const canAutoLoad = hasNextPage && !autoloadCapped;

  const visibleResults = commonOnly ? results.filter(isCommonFactor) : results;

  // Collapse Climatiq rows that differ ONLY by year into one group (same
  // activity/region/data source/LCA boundary/unit type). The Year column then
  // shows how many years are available; using the row imports them all. Other
  // dimensions (region, data source, LCA) stay distinct rows.
  const climatiqGroups = useMemo<FactorGroup[]>(
    () => (provider === "climatiq" ? groupClimatiqResultsByYear(visibleResults) : []),
    [provider, visibleResults],
  );

  // Unified rows for rendering: grouped for Climatiq, raw for local.
  const displayRows = useMemo<FactorGroup[]>(
    () =>
      provider === "climatiq"
        ? climatiqGroups
        : visibleResults.map((r, i) => ({
            key: String(r.id ?? r.factor_code ?? i),
            rep: r,
            years: [],
          })),
    [provider, climatiqGroups, visibleResults],
  );

  // Keep pulling pages while the "common only" filter leaves the view sparse but
  // more pages exist — so a niche-heavy first page doesn't read as "no results".
  useEffect(() => {
    if (canAutoLoad && !isFetchingNextPage && displayRows.length < 8) {
      void fetchNextPage();
    }
  }, [canAutoLoad, isFetchingNextPage, displayRows.length, fetchNextPage]);

  // IntersectionObserver sentinel for infinite scroll (only while under the cap).
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && canAutoLoad && !isFetchingNextPage) {
          void fetchNextPage();
        }
      },
      { root: el.closest("[data-scroll-root]"), rootMargin: "120px" },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [canAutoLoad, isFetchingNextPage, fetchNextPage]);

  // A local pick is an already-saved factor — pass it straight through.
  const pickLocal = (r: Record<string, unknown>) => {
    onPicked([
      {
        provider: "local",
        external_activity_id: String(r.id ?? r.factor_code ?? ""),
        factor_name: String(r.factor_name ?? r.name ?? "Factor"),
        emission_value: Number(r.emission_value ?? 0),
        unit_of_measure: String(r.unit_of_measure ?? ""),
        input_unit_code: String(r.input_unit_code ?? "kg"),
        output_unit_code: String(r.output_unit_code ?? "kg"),
        year: null,
        region: region || null,
        region_name: region || null,
        data_source: r.source != null ? String(r.source) : null,
        external_data_version: null,
        scopes: [],
        sector: r.sector != null ? String(r.sector) : null,
        category: r.category != null ? String(r.category) : null,
        unit_type: r.unit_type != null ? String(r.unit_type) : null,
        source_lca_activity: null,
      },
    ]);
    onOpenChange(false);
  };

  // Estimate one Climatiq variant (a specific year of the representative row) at
  // quantity 1 of its unit type, so co2e is the per-unit factor value and
  // input_unit_code is the matching canonical unit (kg / km / kWh / …). Returns
  // null (and reports) on failure so a group import can skip a bad year.
  const estimateVariant = async (
    r: Record<string, unknown>,
    yr: number | null,
  ): Promise<PickedEmissionFactor | null> => {
    const activityId = String(r.activity_id ?? "");
    if (!activityId) return null;
    const unitType = r.unit_type != null ? String(r.unit_type) : null;
    const lca = r.source_lca_activity != null ? String(r.source_lca_activity) : null;
    const rRegion = r.region != null ? String(r.region) : null;
    const mapping = climatiqUnitTypeMapping(unitType);
    if (unitType && !isSupportedClimatiqUnitType(unitType)) {
      captureMessage(
        `Climatiq unit_type "${unitType}" not mapped; defaulting to Weight/kg (activity_id=${activityId})`,
        "warning",
      );
    }
    try {
      const res = await estimate.mutateAsync({
        provider: "climatiq",
        params: {
          // Pin the EXACT variant: same activity_id has many region/year/LCA
          // rows, so include region, year, and source_lca_activity.
          emission_factor: {
            activity_id: activityId,
            ...(rRegion ? { region: rRegion } : {}),
            ...(yr != null ? { year: yr } : {}),
            ...(lca ? { source_lca_activity: lca } : {}),
          },
          parameters: mapping.estimateParameters,
        },
      });
      const co2e = res.data?.co2e;
      if (co2e == null) {
        captureMessage(
          `Climatiq estimate returned null co2e (activity_id=${activityId}, unit_type=${unitType ?? "?"})`,
          "warning",
        );
        return null;
      }
      return {
        provider: "climatiq",
        external_activity_id: activityId,
        factor_name: String(r.name ?? "Factor"),
        emission_value: co2e,
        // "kg CO2e/<input unit>" (matches system factor convention) instead of
        // Climatiq's bare "kg/kg" which reads as dimensionless in the preview.
        unit_of_measure: `${res.data?.co2e_unit ?? "kg"} CO2e/${mapping.inputUnitCode}`,
        input_unit_code: mapping.inputUnitCode,
        output_unit_code: res.data?.co2e_unit ?? mapping.outputUnitCode,
        year: yr ?? (year ? Number(year) : null),
        region: rRegion ?? region ?? null,
        region_name:
          r.region_name != null ? String(r.region_name) : rRegion ?? region ?? null,
        data_source: r.source != null ? String(r.source) : null,
        external_data_version: data?.pages[0]?.meta?.provider_version ?? null,
        scopes: Array.isArray(r.scopes) ? (r.scopes as string[]) : [],
        sector: r.sector != null ? String(r.sector) : null,
        category: r.category != null ? String(r.category) : null,
        unit_type: unitType,
        source_lca_activity: lca,
      };
    } catch {
      // estimate's onError already toasts; treat as a skipped variant.
      return null;
    }
  };

  // Import a whole group: estimate every available year (or the single
  // year-less variant) and hand all the resulting factors to the consumer.
  const pickGroup = async (group: FactorGroup) => {
    setPendingId(group.key);
    try {
      const yrs = group.years.length ? group.years : [null];
      const picked = (
        await Promise.all(yrs.map((y) => estimateVariant(group.rep, y)))
      ).filter((f): f is PickedEmissionFactor => f != null);
      if (picked.length === 0) {
        toast.errorTemplates.generic();
        return;
      }
      onPicked(picked);
      onOpenChange(false);
    } finally {
      setPendingId(null);
    }
  };

  const pickRow = (group: FactorGroup) =>
    provider === "local" ? pickLocal(group.rep) : pickGroup(group);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <div className="relative min-w-[240px] flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          {/* eslint-disable-next-line jsx-a11y/no-autofocus */}
          <Input
            autoFocus
            placeholder={t("emissions.searchPlaceholder")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select
          value={provider}
          onValueChange={(v) => setProvider(v as EmissionsProvider)}
        >
          <SelectTrigger className="w-[170px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="local">{t("emissions.providerLocal")}</SelectItem>
            <SelectItem value="climatiq">
              {t("emissions.providerClimatiq")}
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      {provider === "climatiq" && (
        <div className="flex flex-wrap items-end gap-3">
          <AutocompleteCombobox
            options={regionOptions}
            value={region}
            onValueChange={setRegion}
            placeholder={t("emissions.regionPlaceholder")}
            emptyMessage={t("emissions.regionEmpty")}
            className="w-[200px]"
          />
          {possibleYears.length > 1 && (
            <Select
              value={year || "any"}
              onValueChange={(v) => setYear(v === "any" ? "" : v)}
            >
              <SelectTrigger className="w-[130px]">
                <SelectValue placeholder={t("emissions.anyYear")} />
              </SelectTrigger>
              <SelectContent>
                {/* Radix forbids an empty-string item value, so "any" is the
                    sentinel for "no year filter". */}
                <SelectItem value="any">{t("emissions.anyYear")}</SelectItem>
                {[...possibleYears]
                  .sort((a, b) => b - a)
                  .map((y) => (
                    <SelectItem key={y} value={String(y)}>
                      {y}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          )}
        </div>
      )}

      {enabled && (
        <div className="flex items-center justify-between gap-3">
          <label
            className="flex items-center gap-2 text-xs text-muted-foreground"
            title={t("emissions.commonOnlyHint")}
          >
            <Checkbox
              checked={commonOnly}
              onCheckedChange={(c) => setCommonOnly(c === true)}
            />
            {t("emissions.commonOnly")}
          </label>
          {results.length > 0 && (
            <span className="text-xs text-muted-foreground">
              {t("emissions.showingCount", { count: displayRows.length })}
            </span>
          )}
        </div>
      )}

      <div
        data-scroll-root
        className="max-h-[420px] overflow-auto rounded-md border"
      >
        {isLoading && enabled ? (
          <div className="space-y-2 p-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-9 w-full" />
            ))}
          </div>
        ) : !enabled ? (
          <Empty message={t("emissions.typeToSearch")} />
        ) : displayRows.length === 0 ? (
          <Empty
            message={
              results.length > 0 && commonOnly
                ? t("emissions.onlyNicheHidden")
                : t("emissions.noResults")
            }
          />
        ) : (
          <table className="w-full border-collapse text-sm">
            <thead className="sticky top-0 z-10 bg-background">
              <tr className="border-b text-left text-xs text-muted-foreground">
                <th className="px-3 py-2 font-medium">{t("emissions.colName")}</th>
                {provider === "climatiq" ? (
                  <>
                    <th className="px-2 py-2 font-medium">{t("emissions.colSource")}</th>
                    <th className="px-2 py-2 font-medium">{t("emissions.colRegion")}</th>
                    <th className="px-2 py-2 font-medium">{t("emissions.colYear")}</th>
                    <th className="px-2 py-2 font-medium">{t("emissions.colUnitType")}</th>
                    <th className="px-2 py-2 font-medium">{t("emissions.colLcaScope")}</th>
                    <th className="px-2 py-2 font-medium">{t("emissions.colCategory")}</th>
                  </>
                ) : (
                  <>
                    <th className="px-2 py-2 font-medium">{t("emissions.colScope")}</th>
                    <th className="px-2 py-2 font-medium">{t("emissions.colUnit")}</th>
                    <th className="px-2 py-2 text-right font-medium">{t("emissions.colValue")}</th>
                  </>
                )}
                <th className="sticky right-0 border-l bg-background px-2 py-2" />
              </tr>
            </thead>
            <tbody>
              {displayRows.map((group) => {
                const r = group.rep;
                const pending = pendingId === group.key;
                const name = String(r.factor_name ?? r.name ?? "Factor");
                // Year cell: a single year shows the year; multiple shows the
                // count ("5 years") — using the row imports them all.
                const yearCount = group.years.length;
                const yearDisplay =
                  yearCount > 1
                    ? t("emissions.yearsAvailable", { count: yearCount })
                    : yearCount === 1
                      ? String(group.years[0])
                      : cell(r.year);
                const yearTitle =
                  yearCount > 1
                    ? `${t("emissions.yearsAvailableHint", { count: yearCount })} (${[...group.years].join(", ")})`
                    : undefined;
                return (
                  <tr
                    key={group.key}
                    className="cursor-pointer border-b last:border-0 hover:bg-muted/50"
                    onClick={() => !pending && pickRow(group)}
                  >
                    <td className="max-w-[260px] px-3 py-2">
                      <span className="block truncate font-medium" title={name}>
                        {name}
                      </span>
                    </td>
                    {provider === "climatiq" ? (
                      <>
                        <td className="max-w-[140px] px-2 py-2 text-muted-foreground">
                          <span className="block truncate" title={cell(r.source)}>
                            {cell(r.source)}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-2 py-2 text-muted-foreground">
                          {cell(r.region_name ?? r.region)}
                        </td>
                        <td
                          className="whitespace-nowrap px-2 py-2 text-muted-foreground"
                          title={yearTitle}
                        >
                          {yearDisplay}
                        </td>
                        <td className="whitespace-nowrap px-2 py-2 text-muted-foreground">{cell(r.unit_type)}</td>
                        <td className="whitespace-nowrap px-2 py-2 text-muted-foreground">
                          {prettyLca(r.source_lca_activity) || "—"}
                        </td>
                        <td className="max-w-[150px] px-2 py-2 text-muted-foreground">
                          <span className="block truncate" title={cell(r.category)}>
                            {cell(r.category)}
                          </span>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="whitespace-nowrap px-2 py-2 text-muted-foreground">{cell(r.scope)}</td>
                        <td className="whitespace-nowrap px-2 py-2 text-muted-foreground">
                          {cell(r.unit_of_measure)}
                        </td>
                        <td className="whitespace-nowrap px-2 py-2 text-right tabular-nums text-muted-foreground">
                          {cell(r.emission_value)}
                        </td>
                      </>
                    )}
                    <td className="sticky right-0 border-l bg-background px-2 py-2 text-right">
                      <Button
                        size="sm"
                        variant="secondary"
                        disabled={pending}
                        onClick={(e) => {
                          e.stopPropagation();
                          pickRow(group);
                        }}
                      >
                        {pending ? (
                          <>
                            <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                            {t("emissions.estimating")}
                          </>
                        ) : (
                          t("emissions.use")
                        )}
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        {/* Infinite-scroll sentinel (only while under the auto-load cap). */}
        {canAutoLoad && <div ref={sentinelRef} aria-hidden className="h-px" />}
        {isFetchingNextPage && (
          <div className="flex items-center justify-center gap-2 p-2 text-xs text-muted-foreground">
            <Loader2 className="h-3 w-3 animate-spin" />
            {t("emissions.loadMore")}
          </div>
        )}
        {/* Past the cap: nudge to refine, but allow loading more on demand. */}
        {hasNextPage && autoloadCapped && !isFetchingNextPage && (
          <div className="flex flex-col items-center gap-1 p-3 text-center text-xs text-muted-foreground">
            <span>{t("emissions.refineHint")}</span>
            <Button variant="ghost" size="sm" onClick={() => void fetchNextPage()}>
              {t("emissions.loadMore")}
            </Button>
          </div>
        )}
        {/* Reached the true end (Climatiq's last page / no more results) — tell
            the user there's nothing further to load. */}
        {enabled &&
          !isLoading &&
          !isFetchingNextPage &&
          !hasNextPage &&
          displayRows.length > 0 && (
            <div className="p-3 text-center text-xs text-muted-foreground">
              {t("emissions.endOfResults", { count: displayRows.length })}
            </div>
          )}
      </div>
      {isFetching && !isFetchingNextPage && enabled && (
        <p className="animate-pulse text-xs text-muted-foreground">
          {t("emissions.searching")}
        </p>
      )}
    </div>
  );
}

function Empty({ message }: { message: string }) {
  return (
    <div className="py-10 text-center text-muted-foreground">
      <Leaf className="mx-auto mb-3 h-10 w-10 opacity-50" />
      <p className="text-sm">{message}</p>
    </div>
  );
}
